/**
 * Kleine Regeln der Admin-Ansicht (seit 16.10.2026), getrennt von den Seiten,
 * damit sie sich ohne Browser prüfen lassen: Datum, Suche, Kennzeichen und
 * wann sich ein Konto hier löschen lässt.
 */

import type { AdminKonto } from './api';
import { locale, t } from './sprache';

export function datum(iso: string | null | undefined, mitZeit = false): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(locale(), {
    day: '2-digit', month: '2-digit', year: 'numeric',
    ...(mitZeit ? { hour: '2-digit', minute: '2-digit' } : {}),
    timeZone: 'Europe/Berlin',
  });
}

export const zahl = (n: number) => new Intl.NumberFormat(locale()).format(n);

export const mehrzahl = (n: number, eins: string, viele: string) => `${zahl(n)} ${n === 1 ? eins : viele}`;

/** Suche über Adresse, Name und Konto-id, ohne Groß- und Kleinschreibung. Leer: alle. */
export function filtereKonten(konten: AdminKonto[], suche: string): AdminKonto[] {
  const s = suche.trim().toLowerCase();
  if (!s) return konten;
  return konten.filter(k => [k.email, k.name, k.id].some(v => (v ?? '').toLowerCase().includes(s)));
}

export interface Kennzeichen { text: string; art: 'neutral' | 'gut' | 'schlecht' | 'marke' }

/** Was an einem Konto auffällt, in der Reihenfolge der Wichtigkeit. */
export function kennzeichen(k: Pick<AdminKonto, 'admin' | 'role' | 'anmeldung' | 'bestaetigt' | 'abo' | 'guthaben'>): Kennzeichen[] {
  const out: Kennzeichen[] = [];
  if (k.admin) out.push({ text: 'Admin', art: 'marke' });
  if (k.role !== 'user') out.push({ text: `role: ${k.role}`, art: 'marke' });
  if (k.abo?.status && k.abo.status !== 'canceled') out.push({ text: `${k.abo.plan ?? t('Abo', 'Subscription')} · ${k.abo.status}`, art: k.abo.status === 'active' ? 'gut' : 'schlecht' });
  if (!k.anmeldung) out.push({ text: t('ohne Anmeldung', 'no sign-in'), art: 'neutral' });
  else if (!k.bestaetigt) out.push({ text: t('nicht bestätigt', 'not confirmed'), art: 'schlecht' });
  if (k.guthaben === null) out.push({ text: t('unbegrenzt', 'unlimited'), art: 'neutral' });
  return out;
}

/**
 * Warum sich ein Konto hier nicht löschen lässt, oder null. Dieselben Regeln
 * wie auf dem Server (admin.ts, konto_loeschen.ts); hier nur, damit der Knopf
 * gar nicht erst erscheint.
 */
export function nichtLoeschbar(k: { id: string; admin: boolean; role: string }, eigeneId: string | null): string | null {
  if (eigeneId && k.id === eigeneId) return t('Das ist dein eigenes Konto. Löschen unter Konto.', 'This is your own account. Delete it under Account.');
  if (k.admin) return t('Admin-Konto. Erst aus SIM_ADMIN_KONTEN nehmen.', 'Admin account. Remove it from SIM_ADMIN_KONTEN first.');
  if (k.role !== 'user') return t(
    `Privilegiertes Konto (role: ${k.role}). Löscht weder die App noch das Skript.`,
    `Privileged account (role: ${k.role}). Neither the app nor the script deletes it.`,
  );
  return null;
}
